import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Mail } from 'lucide-react';
import MarketingHeader from 'src/components/layout/MarketingHeader';
import SiteFooter from 'src/components/layout/SiteFooter';
import { useLocale } from 'src/contexts/LocaleContext';
import {
  BILLING_CYCLE,
  PRICING_PLANS,
  currencyForLocale,
  formatPlanPrice,
  getDisplayMonthlyPrice
} from 'src/config/plans';
import { checkoutPath, saveCheckoutIntent } from 'src/helpers/checkoutIntent';
import { usePublicFeature } from 'src/hooks/usePublicFeature';
import { sendContactMessage } from 'src/services/contactApi';
import toast from 'react-hot-toast';

const asset = (name) => `${process.env.PUBLIC_URL}/landing/${name}`;

const featureIcons = ['calendar.svg', 'users.svg', 'heart.svg', 'link.svg', 'shield.svg', 'activity.svg'];

const previewAppointments = [
  { time: '09:00', name: 'Ana Silva', procedureIndex: 0, confirmed: true },
  { time: '10:30', name: 'Carlos Souza', procedureIndex: 1, confirmed: true },
  { time: '14:00', name: 'Mariana Costa', procedureIndex: 2, confirmed: false }
];

const LandingPage = () => {
  const { t, messages, locale } = useLocale();
  const currency = currencyForLocale(locale);
  const copy = messages.landing;
  const [billingCycle, setBillingCycle] = useState(BILLING_CYCLE.MONTHLY);
  const [contactName, setContactName] = useState('');
  const [contactWhatsapp, setContactWhatsapp] = useState('');
  const [contactSending, setContactSending] = useState(false);
  const { active: paymentActive } = usePublicFeature('paymentActive');

  const pricingPlans = PRICING_PLANS;
  const isAnnual = billingCycle === BILLING_CYCLE.ANNUAL;

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    setContactSending(true);
    try {
      await sendContactMessage({
        name: contactName.trim(),
        whatsapp: contactWhatsapp.trim(),
      });
      setContactName('');
      setContactWhatsapp('');
      toast.success(t('landing.sent'));
    } catch (error) {
      toast.error(error.message || t('landing.sendError'));
    } finally {
      setContactSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf8fc] font-inter text-[#4b5563]">
      <MarketingHeader />

      <section className="bg-white px-5 pb-16 pt-16 sm:px-10 sm:pb-20 sm:pt-24 lg:px-20">
        <div className="mx-auto flex max-w-[1120px] flex-col items-center gap-10">
          <div className="max-w-[900px] text-center">
            <h1 className="font-sora text-4xl font-extrabold leading-[1.15] text-[#0f172a] sm:text-5xl lg:text-[52px]">
              {t('landing.heroLead')} <span className="text-[#7c3aed]">{t('landing.heroHighlight')}</span>
            </h1>
            <p className="mx-auto mt-4 max-w-[900px] text-base leading-[1.6] text-[#4b5563] sm:text-lg">
              {t('landing.heroBody')}
            </p>
          </div>

          <Link
            to="/setup"
            className="inline-flex items-center justify-center rounded-xl bg-[#7c3aed] px-7 py-3.5 font-sora text-[15px] font-semibold text-white shadow-[0px_4px_6px_rgba(124,58,237,0.15)] hover:bg-[#6d28d9]"
          >
            {t('landing.heroCta')}
          </Link>

          <div className="flex h-auto w-full flex-col gap-4 rounded-3xl border border-[#ede9fe] bg-[#faf8fc] p-4 shadow-[0px_24px_24px_rgba(124,58,237,0.03)] md:h-[480px]">
            <div className="flex items-center justify-between rounded-xl bg-white px-4 py-3">
              <div className="flex min-w-0 items-center gap-2">
                <span className="size-2.5 shrink-0 rounded-full bg-[#ff5f57]" />
                <span className="size-2.5 shrink-0 rounded-full bg-[#febc2e]" />
                <span className="size-2.5 shrink-0 rounded-full bg-[#28c840]" />
                <span className="truncate text-xs text-[#9ca3af]">agilodonto.com.br/dashboard</span>
              </div>
              <img src={asset('preview-user.svg')} alt="" width={24} height={24} />
            </div>

            <div className="flex min-h-0 flex-1 gap-4">
              <div className="hidden w-[200px] shrink-0 flex-col gap-3 rounded-xl bg-white p-4 md:flex">
                <div className="flex items-center gap-2.5 rounded-md bg-[#f5f3ff] p-2">
                  <span className="size-4 rounded bg-[#7c3aed]" />
                  <span className="h-3 w-20 rounded-[3px] bg-[#7c3aed]" />
                </div>
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="flex items-center gap-2.5 p-2">
                    <span className="size-4 rounded bg-[#9ca3af]" />
                    <span className="h-3 w-20 rounded-[3px] bg-[#9ca3af]" />
                  </div>
                ))}
              </div>

              <div className="flex min-w-0 flex-1 flex-col gap-5 rounded-xl bg-white p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <h2 className="font-sora text-xl font-bold text-[#0f172a]">{t('landing.previewTitle')}</h2>
                  <span className="rounded-lg bg-[#f5f3ff] px-3 py-1.5 text-xs font-semibold text-[#7c3aed]">
                    {t('landing.filters')}
                  </span>
                </div>
                <div className="flex flex-col gap-2.5">
                  {previewAppointments.map((item) => (
                    <div key={item.time} className="flex items-center justify-between gap-3 rounded-lg bg-[#faf8fc] p-3">
                      <div className="flex min-w-0 items-center gap-4">
                        <span className="shrink-0 text-[13px] font-bold text-[#7c3aed]">{item.time}</span>
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-semibold text-[#0f172a]">{item.name}</span>
                          <span className="block truncate text-xs text-[#4b5563]">{copy.procedures[item.procedureIndex]}</span>
                        </span>
                      </div>
                      <span
                        className={`shrink-0 rounded-md px-2.5 py-1 text-[11px] font-semibold ${
                          item.confirmed
                            ? 'bg-[rgba(16,185,129,0.1)] text-[#10b981]'
                            : 'bg-[#ede9fe] text-[#4b5563]'
                        }`}
                      >
                        {item.confirmed ? t('landing.statusConfirmed') : t('landing.statusWaiting')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="px-5 py-20 sm:px-10 lg:px-20 lg:py-[120px]">
        <div className="mx-auto flex max-w-[1120px] flex-col gap-16">
          <div className="mx-auto flex max-w-[640px] flex-col items-center gap-3 text-center">
            <p className="font-sora text-sm font-bold uppercase text-[#7c3aed]">{t('landing.featuresEyebrow')}</p>
            <h2 className="font-sora text-3xl font-extrabold text-[#0f172a] sm:text-4xl">{t('landing.featuresTitle')}</h2>
            <p className="text-base text-[#4b5563]">
              {t('landing.featuresBody')}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {copy.features.map((feature, index) => (
              <article key={feature.title} className="flex flex-col gap-4 rounded-2xl border border-[#ede9fe] bg-white p-6">
                <span className="flex size-12 items-center justify-center rounded-xl bg-[#f5f3ff]">
                  <img src={asset(featureIcons[index])} alt="" width={22} height={22} />
                </span>
                <div>
                  <h3 className="font-sora text-lg font-semibold text-[#0f172a]">{feature.title}</h3>
                  <p className="mt-2 text-sm leading-normal text-[#4b5563]">{feature.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-[#ede9fe] bg-white px-5 py-20 sm:px-10 lg:px-20 lg:py-[100px]">
        <div className="mx-auto flex max-w-[1120px] flex-col gap-14">
          <div className="mx-auto max-w-[720px] text-center">
            <h2 className="font-sora text-3xl font-extrabold text-[#0f172a] sm:text-[32px]">
              {t('landing.dailyTitle')}
            </h2>
            <p className="mt-3 text-base text-[#4b5563]">
              {t('landing.dailyBody')}
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {copy.daily.map((item) => (
              <article
                key={item.title}
                className="rounded-2xl border border-[#ede9fe] bg-white p-7 shadow-[0px_6px_8px_rgba(30,27,75,0.02)]"
              >
                <h3 className="font-sora text-lg font-semibold text-[#0f172a]">{item.title}</h3>
                <p className="mt-3 text-sm leading-normal text-[#4b5563]">{item.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="px-5 py-20 sm:px-10 lg:px-20 lg:py-[120px]">
        <div className="mx-auto flex max-w-[1120px] flex-col gap-14">
          <div className="text-center">
            <h2 className="font-sora text-3xl font-extrabold text-[#0f172a] sm:text-4xl">
              {t('landing.pricingTitle')}
            </h2>
            <p className="mt-4 text-base text-[#4b5563]">
              {t('landing.pricingBody')}
            </p>
            {!paymentActive && (
              <p className="mt-4 text-[13px] font-semibold text-[#7c3aed]">
                {t('landing.trialNote')}
              </p>
            )}
            <div className="mt-4 inline-flex items-center gap-1 rounded-full bg-[#ede9fe] p-1">
              <button
                type="button"
                onClick={() => setBillingCycle(BILLING_CYCLE.MONTHLY)}
                className={`rounded-full px-4 py-2 text-[13px] font-semibold ${
                  !isAnnual ? 'bg-white text-[#0f172a]' : 'text-[#4b5563]'
                }`}
              >
                {t('landing.monthly')}
              </button>
              <button
                type="button"
                onClick={() => setBillingCycle(BILLING_CYCLE.ANNUAL)}
                className={`inline-flex items-center gap-1 rounded-full px-4 py-2 text-[13px] font-semibold ${
                  isAnnual ? 'bg-white text-[#0f172a]' : 'text-[#4b5563]'
                }`}
              >
                {t('landing.annual')}
                <span className="rounded-md bg-[#7c3aed] px-1.5 py-0.5 text-[10px] font-bold text-white">-20%</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-2 xl:grid-cols-4">
            {pricingPlans.map((plan) => {
              const displayPrice = getDisplayMonthlyPrice(plan, billingCycle, currency);
              const planCopy = messages.plans[plan.id] || plan;
              const ctaClass = plan.popular
                ? 'bg-[#7c3aed] text-white shadow-[0px_4px_6px_rgba(124,58,237,0.15)] hover:bg-[#6d28d9]'
                : 'border border-[#ede9fe] bg-white text-[#7c3aed] shadow-[0px_4px_6px_rgba(0,0,0,0.02)] hover:bg-[#f5f3ff]';

              return (
                <article
                  key={plan.id}
                  className={`relative flex flex-col gap-6 rounded-[20px] bg-white p-8 ${
                    plan.popular
                      ? 'border-2 border-[#7c3aed] shadow-[0px_12px_12px_rgba(124,58,237,0.07)]'
                      : 'border border-[#ede9fe] shadow-[0px_12px_12px_rgba(0,0,0,0.02)]'
                  }`}
                >
                  {plan.popular && (
                    <span className="absolute left-1/2 top-[-16px] -translate-x-1/2 rounded-[20px] bg-[#7c3aed] px-4 py-1 font-sora text-[11px] font-bold text-white">
                      {t('landing.popular')}
                    </span>
                  )}

                  <div className="text-center">
                    <h3 className="font-sora text-lg font-semibold text-[#0f172a]">{planCopy.name}</h3>
                    <p className="mt-2 flex items-baseline justify-center gap-1">
                      <span className="font-sora text-[28px] font-bold text-[#0f172a]">
                        {formatPlanPrice(displayPrice, currency)}
                      </span>
                      <span className="text-[13px] text-[#9ca3af]">{t('landing.perMonth')}</span>
                    </p>
                    {isAnnual && (
                      <p className="mt-1 text-xs text-[#7c3aed]">
                        {t('landing.annualNote', { price: formatPlanPrice(getDisplayMonthlyPrice(plan, BILLING_CYCLE.MONTHLY, currency), currency) })}
                      </p>
                    )}
                    <p className="mt-2 text-xs text-[#4b5563]">{planCopy.description}</p>
                  </div>

                  <div className="border-t border-[#ede9fe]" />

                  <ul className="flex flex-col gap-3.5">
                    {(planCopy.features || plan.marketingFeatures).map((feature) => (
                      <li key={feature} className="flex items-center gap-2.5 text-[13px] text-[#4b5563]">
                        <span
                          className={`flex size-[18px] shrink-0 items-center justify-center rounded-[9px] ${
                            plan.popular ? 'bg-[#f5f3ff]' : 'bg-[rgba(16,185,129,0.1)]'
                          }`}
                        >
                          <Check
                            className={`size-2.5 ${plan.popular ? 'text-[#7c3aed]' : 'text-[#10b981]'}`}
                            strokeWidth={3}
                            aria-hidden="true"
                          />
                        </span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="pt-2">
                    {paymentActive ? (
                      <Link
                        to={checkoutPath(plan.id, billingCycle)}
                        onClick={() => saveCheckoutIntent(plan.id, billingCycle)}
                        className={`block w-full rounded-xl py-3.5 text-center font-sora text-[15px] font-semibold ${ctaClass}`}
                      >
                        {t('landing.subscribe')}
                      </Link>
                    ) : (
                      <Link
                        to="/setup"
                        className={`block w-full rounded-xl py-3.5 text-center font-sora text-[15px] font-semibold ${ctaClass}`}
                      >
                        {t('landing.freeTrial')}
                      </Link>
                    )}
                  </div>
                </article>
              );
            })}
          </div>

          <div className="text-center">
            <h3 className="font-sora text-2xl font-bold text-[#0f172a]">{t('landing.compareTitle')}</h3>
            <p className="mt-2 text-sm text-[#64748b]">{t('landing.compareBody')}</p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <article className="rounded-[20px] border border-[#e2e8f0] bg-white p-7">
              <h3 className="font-sora text-[22px] font-bold text-[#0f172a]">{t('upgrade.freeName')}</h3>
              <p className="mt-2 font-sora text-[40px] font-bold leading-none text-[#0f172a]">{t('upgrade.freePrice')}</p>
              <p className="mt-4 text-sm text-[#64748b]">{t('landing.compareFreeNote')}</p>
              <ul className="mt-4 space-y-2 text-sm text-[#0f172a]">
                <li>✓ {t('upgrade.freePointAgenda').replace(/^·\s*/, '')}</li>
                <li>✓ {t('upgrade.freePointPatient').replace(/^·\s*/, '')}</li>
                <li className="text-[#94a3b8]">– {t('upgrade.paidPointClinical').replace(/^·\s*/, '')}</li>
                <li className="text-[#94a3b8]">– {t('upgrade.benefitTabs')}</li>
              </ul>
            </article>
            <article className="rounded-[20px] border-[1.5px] border-[#7c3aed] bg-[#f5f3ff] p-7">
              <span className="inline-flex rounded-full bg-[#7c3aed] px-2.5 py-0.5 text-[11px] font-bold text-white">
                {t('landing.comparePaidBadge')}
              </span>
              <h3 className="mt-3 font-sora text-[22px] font-bold text-[#0f172a]">{t('upgrade.paidName')}</h3>
              <p className="mt-2 font-sora text-[40px] font-bold leading-none text-[#0f172a]">{t('upgrade.paidPrice')}</p>
              <p className="mt-4 text-sm text-[#64748b]">{t('landing.comparePaidNote')}</p>
              <ul className="mt-4 space-y-2 text-sm text-[#0f172a]">
                <li>✓ {t('upgrade.benefitOdontogram')}</li>
                <li>✓ {t('upgrade.benefitTabs')}</li>
                <li>✓ {t('upgrade.benefitSuggestions')}</li>
                <li>✓ {t('upgrade.paidPointStorage').replace(/^·\s*/, '')}</li>
              </ul>
            </article>
          </div>
          <p className="text-center text-xs text-[#94a3b8]">{t('landing.compareFoot')}</p>
        </div>
      </section>

      <section id="contact" className="border-t border-[#ede9fe] bg-white px-5 py-16 sm:px-10 lg:px-20 lg:py-20">
        <div className="mx-auto grid max-w-[1120px] items-start gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <p className="font-sora text-sm font-bold uppercase text-[#7c3aed]">{t('landing.contactEyebrow')}</p>
            <h2 className="mt-3 font-sora text-3xl font-extrabold text-[#0f172a] sm:text-[32px]">{t('landing.contactTitle')}</h2>
            <p className="mt-3 max-w-xl text-[15px] leading-normal text-[#4b5563]">
              {t('landing.contactBody')}
            </p>
            <a href="mailto:contato@agilodonto.com.br" className="mt-6 inline-flex items-center gap-3 text-sm font-medium text-[#0f172a]">
              <span className="flex size-9 items-center justify-center rounded-lg bg-[#f5f3ff]">
                <Mail className="size-[18px] text-[#7c3aed]" aria-hidden="true" />
              </span>
              contato@agilodonto.com.br
            </a>
          </div>

          <form
            onSubmit={handleContactSubmit}
            className="flex w-full flex-col gap-5 rounded-2xl border border-[#ede9fe] bg-[#faf8fc] p-8 lg:max-w-[480px] lg:justify-self-end"
          >
            <h3 className="font-sora text-lg font-bold text-[#0f172a]">{t('landing.formTitle')}</h3>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-[#4b5563]">{t('landing.yourName')}</span>
              <input
                required
                value={contactName}
                onChange={(event) => setContactName(event.target.value)}
                placeholder={t('landing.namePlaceholder')}
                className="rounded-lg border border-[#ede9fe] bg-white p-3 text-sm text-[#0f172a] outline-none placeholder:text-[#9ca3af] focus:border-[#7c3aed]"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-[#4b5563]">{t('landing.whatsapp')}</span>
              <input
                required
                value={contactWhatsapp}
                onChange={(event) => setContactWhatsapp(event.target.value)}
                placeholder="(11) 99999-9999"
                inputMode="tel"
                className="rounded-lg border border-[#ede9fe] bg-white p-3 text-sm text-[#0f172a] outline-none placeholder:text-[#9ca3af] focus:border-[#7c3aed]"
              />
            </label>
            <button
              type="submit"
              disabled={contactSending}
              className="rounded-xl bg-[#7c3aed] px-7 py-3.5 font-sora text-[15px] font-semibold text-white shadow-[0px_4px_6px_rgba(124,58,237,0.15)] hover:bg-[#6d28d9] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {contactSending ? t('landing.sending') : t('landing.send')}
            </button>
          </form>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default LandingPage;
